// ========================================
// Program View - Subject Management Logic
// ========================================

Object.assign(ProgramView, {
    initializeVisibleSubjects() {
        if (!this.currentFormation) return;

        // 1. Get Saved
        let savedSubjects = [];
        const key = `program_visible_subjects_${this.currentFormation.id}`;
        const saved = BrowserState.getItem(key);
        if (saved) {
            try {
                savedSubjects = JSON.parse(saved);
            } catch (e) {
                console.error('Error parsing saved visible subjects', e);
            }
        }

        // 2. Load Active Classes (Source of Truth for this formation)
        const classes = DataManager.getClassesByFormation(this.currentFormation.id);
        const activeClassSubjectIds = [...new Set(classes.map(c => String(c.subjectId)))];

        // 3. Migrate and Sync
        // First, ensure all saved items are objects and have instanceIds
        let synchronized = savedSubjects.map(item => {
            if (typeof item === 'string') {
                return {
                    id: String(item),
                    scheduleId: null,
                    instanceId: (typeof Utils.generateUUID === 'function') ? Utils.generateUUID() : Date.now() + Math.random().toString()
                };
            }
            return {
                id: String(item.id),
                scheduleId: item.scheduleId || null,
                instanceId: item.instanceId || ((typeof Utils.generateUUID === 'function') ? Utils.generateUUID() : Date.now() + Math.random().toString())
            };
        });

        // 3b. Deduplicate: Remove entries with same (id + scheduleId)
        // We keep the first occurrence as it's likely the oldest/most stable
        const uniqueItems = [];
        const seenKeys = new Set();
        synchronized.forEach(item => {
            const key = `${item.id}_${item.scheduleId || 'default'}`;
            // Exception: If it's the currently selected instance, we must keep it
            const isSelected = this.selectedInstanceId && String(item.instanceId) === String(this.selectedInstanceId);
            if (!seenKeys.has(key) || isSelected) {
                uniqueItems.push(item);
                if (!isSelected) seenKeys.add(key);
            }
        });
        synchronized = uniqueItems;

        // Add any active that are missing from the list
        activeClassSubjectIds.forEach(id => {
            const idStr = String(id);
            const exists = synchronized.some(item => String(item.id) === idStr);
            if (!exists) {
                synchronized.push({
                    id: idStr,
                    scheduleId: null,
                    instanceId: (typeof Utils.generateUUID === 'function') ? Utils.generateUUID() : Date.now() + Math.random().toString()
                });
            }
        });

        this.visibleSubjects = synchronized;

        // 4. Save back to ensure consistency
        BrowserState.setItem(key, JSON.stringify(this.visibleSubjects));
        console.log(`[DEBUG] Initialized visible subjects for ${this.currentFormation.id}:`, this.visibleSubjects.length);
    },

    // ========================================
    // Observaciones de horario (columna OBS.)
    // ========================================

    /**
     * Horario "general" de la formación actual — el que se da por supuesto para
     * todas sus asignaturas (p.ej. Dirección 1º: 15:00-18:00). Es la referencia
     * contra la que se comparan las clases para decidir si hay observación.
     */
    getFormationDefaultSchedule() {
        const f = this.currentFormation;
        if (!f || !f.defaultStartTime || !f.defaultEndTime) return null;
        return { startTime: f.defaultStartTime, endTime: f.defaultEndTime };
    },

    /**
     * Devuelve las observaciones de horario de una fila de la lista de
     * asignaturas: los tramos horarios realmente asignados este mes que NO
     * coinciden con el horario general de la formación, agrupados por tramo.
     *
     * - `activeSchedule`: si la fila tiene un horario concreto elegido, solo
     *   cuentan las clases de ese tramo (una misma asignatura puede tener
     *   varias filas, una por horario).
     * - `siblingScheduleTimes`: tramos ya "reclamados" por otras filas de la
     *   misma asignatura, para que la fila sin horario elegido no los repita.
     */
    getScheduleObservations(subject, monthClasses, activeSchedule, siblingScheduleTimes) {
        const base = this.getFormationDefaultSchedule();
        if (!base) return []; // sin horario general no hay contra qué comparar

        let classes = (monthClasses || []).filter(c =>
            String(c.subjectId) === String(subject.id) && c.startTime && c.endTime
        );

        if (activeSchedule && activeSchedule.startTime && activeSchedule.endTime) {
            classes = classes.filter(c =>
                c.startTime === activeSchedule.startTime && c.endTime === activeSchedule.endTime
            );
        } else if (siblingScheduleTimes && siblingScheduleTimes.size > 0) {
            classes = classes.filter(c => !siblingScheduleTimes.has(`${c.startTime}-${c.endTime}`));
        }

        const groups = new Map();
        classes.forEach(c => {
            if (c.startTime === base.startTime && c.endTime === base.endTime) return;
            const key = `${c.startTime}-${c.endTime}`;
            if (!groups.has(key)) {
                groups.set(key, { startTime: c.startTime, endTime: c.endTime, days: [] });
            }
            groups.get(key).days.push(c.startDate);
        });

        const observations = Array.from(groups.values());

        // Horario propio elegido en la fila que todavía no tiene ningún día
        // asignado este mes: se avisa igualmente de que será distinto.
        if (activeSchedule && activeSchedule.startTime && activeSchedule.endTime &&
            !groups.has(`${activeSchedule.startTime}-${activeSchedule.endTime}`) &&
            (activeSchedule.startTime !== base.startTime || activeSchedule.endTime !== base.endTime)) {
            observations.push({
                startTime: activeSchedule.startTime,
                endTime: activeSchedule.endTime,
                days: []
            });
        }

        observations.forEach(o => o.days.sort());
        observations.sort((a, b) => a.startTime.localeCompare(b.startTime));
        return observations;
    },

    renderScheduleObservations(observations) {
        const base = this.getFormationDefaultSchedule();
        if (!base || !observations || observations.length === 0) return '';

        return observations.map(o => {
            const dayNumbers = o.days.map(d => parseInt(String(d).split('-')[2], 10));
            const isPending = o.days.length === 0;
            const countLabel = isPending
                ? 'sin días aún'
                : `${o.days.length} ${o.days.length === 1 ? 'día' : 'días'}`;
            const tooltip = isPending
                ? `Horario propio ${o.startTime}-${o.endTime}, distinto del general de la formación (${base.startTime}-${base.endTime}). Todavía sin días asignados este mes.`
                : `Horario ${o.startTime}-${o.endTime} en vez del general de la formación (${base.startTime}-${base.endTime}). Días: ${dayNumbers.join(', ')}`;

            return `
                <span class="subject-obs-chip${isPending ? ' is-pending' : ''}" title="${Utils.sanitizeHTML(tooltip)}">
                    <i class="fas fa-clock"></i>
                    <strong>${o.startTime}-${o.endTime}</strong>
                    <em>${countLabel}</em>
                </span>
            `;
        }).join('');
    },

    renderSubjects() {
        try {
            const list = document.getElementById('subjects-list');
            if (!list || !this.currentFormation) return;

            const isStudent = AuthManager.isStudent() || !AuthManager.canManageModule('plan-estudios');
            let allSubjects = DataManager.getSubjectsByFormation(this.currentFormation.id) || [];
            if (AuthManager.isProfessor() && !AuthManager.hasModule('plan-estudios')) {
                const professorId = AuthManager.getProfessorId();
                allSubjects = allSubjects.filter(s => s.professorId === professorId);
            }

            // Ensure visibleSubjects is initialized
            if (!this.visibleSubjects || this.visibleSubjects.length === 0) {
                this.initializeVisibleSubjects();
            }

            // Stabilize: If selectedInstanceId is set but not found in visibleSubjects, try to recover by id
            if (this.selectedInstanceId && !this.visibleSubjects.some(i => String(i.instanceId) === String(this.selectedInstanceId))) {
                console.log('[DEBUG] Selected instance lost, attempting recovery by subject ID:', this.selectedSubjectId);
                const recovered = this.visibleSubjects.find(i => String(i.id) === String(this.selectedSubjectId));
                if (recovered) {
                    this.selectedInstanceId = recovered.instanceId;
                } else {
                    this.selectedInstanceId = null;
                    this.selectedSubjectId = null;
                }
            }

            // Month Filter Prefix
            const year = this.currentMonth.getFullYear();
            const month = this.currentMonth.getMonth();
            const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

            const allFormationClasses = DataManager.getClassesByFormation(this.currentFormation.id);
            const assignedClasses = allFormationClasses.filter(c => c.startDate && c.startDate.startsWith(monthPrefix));
            const assignedSubjectIds = new Set(assignedClasses.map(c => String(c.subjectId)));

            console.log(`[DEBUG] Rendering month: ${monthPrefix}, Found ${assignedSubjectIds.size} assigned subjects`);

            // Map and Filter visible subject instances
            const visibleInstances = this.visibleSubjects.map(item => {
                const subject = allSubjects.find(s => String(s.id) === String(item.id));
                if (!subject) return null;

                const isAssigned = assignedSubjectIds.has(String(item.id));
                const isSelected = String(this.selectedInstanceId) === String(item.instanceId);

                // IMPORTANT: Only show if assigned in the VISIBLE MONTH OR currently selected to be added
                if (!isAssigned && !isSelected) return null;

                return { ...subject, _instanceId: item.instanceId, _scheduleId: item.scheduleId };
            }).filter(Boolean);

            // DEDUPLICATION: Keep only one instance per subject+schedule combination
            // This allows the same subject with different schedules in the same month
            const uniqueSubjects = new Map();
            visibleInstances.forEach(subject => {
                // Use combination of subjectId + scheduleId as unique key
                const key = `${subject.id}_${subject._scheduleId || 'no-schedule'}`;
                const existing = uniqueSubjects.get(key);

                // If no existing instance with this exact combination, add it
                if (!existing) {
                    uniqueSubjects.set(key, subject);
                }
            });

            const dedupedInstances = Array.from(uniqueSubjects.values());

            // Tramos horarios que ya "reclama" cada fila con horario propio, para
            // que la fila sin horario elegido de la misma asignatura no los repita
            // en sus observaciones.
            const siblingScheduleTimes = new Map();
            dedupedInstances.forEach(s => {
                if (!s._scheduleId || !s.schedules) return;
                const sch = s.schedules.find(x => x.id === s._scheduleId);
                if (!sch || !sch.startTime || !sch.endTime) return;
                const key = String(s.id);
                if (!siblingScheduleTimes.has(key)) siblingScheduleTimes.set(key, new Set());
                siblingScheduleTimes.get(key).add(`${sch.startTime}-${sch.endTime}`);
            });

            let html = '';
            dedupedInstances.forEach((subject, index) => {
                const isSelected = String(this.selectedInstanceId) === String(subject._instanceId);
                const professor = DataManager.getProfessor(subject.professorId);
                const professorName = professor ? `${professor.firstName} ${professor.lastName}` : 'Sin profesor';

                let activeSchedule = null;
                if (subject._scheduleId && subject.schedules) {
                    activeSchedule = subject.schedules.find(s => s.id === subject._scheduleId);
                }
                const color = activeSchedule ? activeSchedule.color : this.getColorForSubject(subject.id, index);
                const type = subject.type || 'COMÚN';

                // Observaciones: horarios de este mes distintos al general de la formación
                const observations = this.getScheduleObservations(
                    subject,
                    assignedClasses,
                    activeSchedule,
                    siblingScheduleTimes.get(String(subject.id))
                );
                const observationsHtml = this.renderScheduleObservations(observations);

                const monthClassrooms = [...new Set([
                    ...assignedClasses.filter(c => String(c.subjectId) === String(subject.id)).map(c => c.classroom).filter(Boolean),
                    ...(subject.classroom ? [subject.classroom] : [])
                ])];
                let scheduleControl = '';
                if (subject.schedules && subject.schedules.length > 0) {
                    scheduleControl = isStudent
                        ? (activeSchedule ? `<span class="subject-schedule-name">${Utils.sanitizeHTML(activeSchedule.name)} (${Utils.sanitizeHTML(activeSchedule.startTime)}-${Utils.sanitizeHTML(activeSchedule.endTime)})</span>` : '')
                        : `
                            <select class="subject-schedule-select" aria-label="Horario de ${Utils.sanitizeHTML(subject.name)}"
                                    ${UIEvents.attr("click", function(event,captured){event.stopPropagation();}, [])}
                                    ${UIEvents.attr("change", function(event,captured){ProgramView.handleSubjectScheduleChange(String(captured[0]),this.value);}, [subject._instanceId])}>
                                <option value="">Horario…</option>
                                ${subject.schedules.map(s => `
                                    <option value="${Utils.sanitizeHTML(s.id)}" ${subject._scheduleId === s.id ? 'selected' : ''}>
                                        ${Utils.sanitizeHTML(s.name)} (${Utils.sanitizeHTML(s.startTime)}-${Utils.sanitizeHTML(s.endTime)})
                                    </option>
                                `).join('')}
                            </select>`;
                }

                html += `
                    <div class="subject-item ${isSelected ? 'selected' : ''}"
                        draggable="${isStudent ? 'false' : 'true'}"
                        data-instance-id="${Utils.sanitizeHTML(subject._instanceId)}"
                        ${isStudent ? '' : `${UIEvents.attr("click", function(event,captured){ProgramView.toggleSubjectSelection(String(captured[0]));}, [subject._instanceId])}`}>
                        <div class="drag-handle">${isStudent ? '' : '<i class="fas fa-grip-vertical"></i>'}</div>
                        <div class="pg-cell-color">
                            <span class="subject-color" style="background-color: ${color};"
                                ${isStudent ? '' : `title="Cambiar color" ${UIEvents.attr("click", function(event,captured){ProgramView.openColorPicker(event,String(captured[0]),String(captured[1]));}, [subject.id, subject._instanceId])}`}></span>
                        </div>
                        <div class="subject-name-professor">
                            <span class="subject-name">${Utils.sanitizeHTML(subject.name)}</span>
                            <span class="subject-professor ${professor ? '' : 'is-missing'}"
                                ${isStudent ? '' : `title="Cambiar profesor" ${UIEvents.attr("click", function(event,captured){ProgramView.openProfessorPicker(event,String(captured[0]));}, [subject.id])}`}>
                                ${Utils.sanitizeHTML(professorName)}
                            </span>
                        </div>
                        <div class="pg-cell-room">
                            <span class="subject-classroom-badge"
                                ${isStudent
                                    ? `title="Ver información del aula" ${UIEvents.attr("click", function(event,captured){ProgramView.showClassroomInfoFromSubject(event,String(captured[0]));}, [subject.id])}`
                                    : `title="Ver / asignar aula" ${UIEvents.attr("click", function(event,captured){ProgramView.openClassroomPickerForSubject(event,String(captured[0]),String(captured[1]));}, [subject.id, subject._instanceId])}`}>
                                <i class="fas fa-door-open"></i>
                                ${monthClassrooms.length > 0 ? Utils.sanitizeHTML(monthClassrooms.join(', ')) : (isStudent ? 'Sin aula' : 'Asignar aula')}
                            </span>
                        </div>
                        <div class="pg-cell-type"><span class="subject-type-badge ${Utils.sanitizeHTML(type.toLowerCase())}">${Utils.sanitizeHTML(type)}</span></div>
                        <div class="subject-schedule">${scheduleControl || '<span class="no-schedule">—</span>'}</div>
                        <div class="pg-cell-obs">${observationsHtml}</div>
                        <div class="subject-actions">
                            ${isStudent ? '' : `
                            <button type="button" class="remove-btn" title="Quitar de la lista" aria-label="Quitar ${Utils.sanitizeHTML(subject.name)} de la lista" ${UIEvents.attr("click", function(event,captured){ProgramView.removeVisibleSubject(String(captured[0]),event);}, [subject._instanceId])}>
                                <i class="fas fa-times"></i>
                            </button>`}
                        </div>
                    </div>
                `;
            });

            const countEl = document.getElementById('pg-subjects-count');
            if (countEl) countEl.textContent = String(dedupedInstances.length);

            // If we are in "Add mode", show the selector
            if (this.isSelectingNewSubject) {
                html += `
                    <div class="subject-item new-subject-row" style="background: var(--bg-card); padding: 1rem; border: 1px dashed var(--border-color);">
                        <div style="width: 100%; display: flex; gap: 0.5rem;">
                            <select class="form-input" ${UIEvents.attr("change", function(event,captured){ProgramView.handleSubjectSelectChange(this);}, [])} style="flex: 1;">
                                <option value="">Selecciona una asignatura para añadir...</option>
                                ${allSubjects.map(s => `<option value="${Utils.sanitizeHTML(s.id)}">${Utils.sanitizeHTML(s.name)}</option>`).join('')}
                            </select>
                            <button class="btn-icon" ${UIEvents.attr("click", function(event,captured){ProgramView.handleCancelSubjectSelect();}, [])} style="color: var(--text-secondary);">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                `;
            }

            SafeHTML.set(list, html || '<p class="pg-empty">No hay asignaturas en este mes.</p>');
        } catch (error) {
            console.error('Error in renderSubjects:', error);
        }
    },

    toggleSubjectSelection(instanceId) {
        if (this.selectedInstanceId === instanceId) {
            this.selectedInstanceId = null;
            this.selectedSubjectId = null;
        } else {
            this.selectedInstanceId = instanceId;
            const item = this.visibleSubjects.find(i => String(i.instanceId) === String(instanceId));
            if (item) {
                this.selectedSubjectId = item.id;
            } else {
                console.warn('Could not find instance', instanceId);
            }
        }
        this.renderCalendar();
        this.renderSubjects();
    },

    handleSubjectSelectChange(selectElement) {
        const subjectId = String(selectElement.value);
        if (!subjectId) return;

        if (!this.visibleSubjects) this.visibleSubjects = [];

        // Prevent Duplicates: If a "default" instance (no scheduleId) of this subject already exists, use it
        const existing = this.visibleSubjects.find(i => String(i.id) === subjectId && !i.scheduleId);

        if (existing) {
            this.selectedInstanceId = existing.instanceId;
            this.selectedSubjectId = subjectId;
            console.log('[DEBUG] Subject already in list, re-selecting:', subjectId);
        } else {
            const newInstanceId = Utils.generateUUID();
            this.visibleSubjects.push({
                id: subjectId,
                scheduleId: null,
                instanceId: newInstanceId
            });
            this.selectedInstanceId = newInstanceId;
            this.selectedSubjectId = subjectId;
        }

        const key = `program_visible_subjects_${this.currentFormation.id}`;
        BrowserState.setItem(key, JSON.stringify(this.visibleSubjects));

        this.isSelectingNewSubject = false;
        this.renderCalendar();
    },

    handleSubjectScheduleChange(instanceId, newScheduleId) {
        if (!this.visibleSubjects) return;
        const item = this.visibleSubjects.find(i => String(i.instanceId) === String(instanceId));
        if (item) {
            item.scheduleId = newScheduleId;
            const key = `program_visible_subjects_${this.currentFormation.id}`;
            BrowserState.setItem(key, JSON.stringify(this.visibleSubjects));
            this.renderSubjects();
        }
    },

    handleCancelSubjectSelect() {
        this.isSelectingNewSubject = false;
        this.renderSubjects();
    },

    removeVisibleSubject(instanceId, event) {
        if (event) event.stopPropagation();

        const targetItem = this.visibleSubjects.find(i => String(i.instanceId) === String(instanceId));
        if (!targetItem) return;

        const subject = DataManager.getSubject(targetItem.id);
        const subjectName = subject ? subject.name : 'la asignatura';

        SafeHTML.fire({
            title: '¿Quitar asignatura?',
            html: `Se desvinculará <strong>${Utils.sanitizeHTML(subjectName)}</strong> y se eliminarán todas sus sesiones programadas en este mes.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, quitar y eliminar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                if (this.currentFormation) {
                    const year = this.currentMonth.getFullYear();
                    const month = this.currentMonth.getMonth();
                    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

                    // Delete classes for this subject and formation in the active month
                    const classesToDelete = DataManager.getClassesByFormation(this.currentFormation.id)
                        .filter(c => String(c.subjectId) === String(targetItem.id) && c.startDate && c.startDate.startsWith(monthPrefix));

                    classesToDelete.forEach(c => {
                        DataManager.deleteClass(c.id);
                    });
                }

                this.visibleSubjects = this.visibleSubjects.filter(i => String(i.instanceId) !== String(instanceId));
                const key = `program_visible_subjects_${this.currentFormation.id}`;
                BrowserState.setItem(key, JSON.stringify(this.visibleSubjects));

                if (String(this.selectedInstanceId) === String(instanceId)) {
                    this.selectedInstanceId = null;
                    this.selectedSubjectId = null;
                }
                
                this.renderCalendar();
                this.renderSubjects();
                this.updateSummary();
                Utils.showToast('Asignatura y clases eliminadas del mes', 'success');
            }
        });
    },

    getColorForSubject(subjectId, index = 0) {
        if (!this.subjectColors) this.subjectColors = {};
        if (!this.subjectColors[subjectId]) {
            const subject = DataManager.getSubject(subjectId);
            if (subject && subject.color) {
                this.subjectColors[subjectId] = subject.color;
            } else {
                const colors = ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#10B981', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E'];
                const idString = String(subjectId);
                let hash = 0;
                for (let i = 0; i < idString.length; i++) hash = ((hash << 5) - hash) + idString.charCodeAt(i);
                const colorIdx = Math.abs(hash) % colors.length;
                this.subjectColors[subjectId] = colors[colorIdx];
            }
        }
        return this.subjectColors[subjectId];
    },

    openColorPicker(event, subjectId, instanceId) {
        event.stopPropagation();

        // Get current color
        const currentColor = this.getColorForSubject(subjectId);

        const content = `
            <div style="padding: 1rem;">
                <div id="program-color-picker"></div>
                <input type="hidden" id="program-selected-color" value="${currentColor}">
                
                <div style="display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid var(--border-color);">
                    <button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button class="btn btn-primary" ${UIEvents.attr("click", function(event,captured){ProgramView.saveColorFromPicker(String(captured[0]),String(captured[1]));}, [subjectId, instanceId || ''])}>
                        Guardar
                    </button>
                </div>
            </div>
        `;

        Utils.openModal('Seleccionar Color', content);

        // Initialize color picker after modal is rendered
        setTimeout(() => {
            const colorInput = document.getElementById('program-selected-color');
            if (colorInput) {
                new ColorPicker('program-color-picker', currentColor, (color) => {
                    colorInput.value = color;
                });
            }
        }, 100);
    },

    saveColorFromPicker(subjectId, instanceId) {
        const colorInput = document.getElementById('program-selected-color');
        if (colorInput && colorInput.value) {
            this.updateSubjectColor(subjectId, colorInput.value, instanceId);
            Utils.closeModal();
        }
    },

    updateSubjectColor(subjectId, color, instanceId) {
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        // Update schedule color if applicable
        if (instanceId && this.visibleSubjects) {
            const instance = this.visibleSubjects.find(i => String(i.instanceId) === String(instanceId));
            if (instance && instance.scheduleId && subject.schedules) {
                const schedule = subject.schedules.find(s => s.id === instance.scheduleId);
                if (schedule) {
                    schedule.color = color;
                    DataManager.updateSubject(subjectId, { schedules: subject.schedules });
                }
            }
        }

        DataManager.updateSubject(subjectId, { color: color });
        if (this.subjectColors) this.subjectColors[subjectId] = color;

        Utils.closeModal();
        this.renderCalendar();
    },

    openProfessorPicker(event, subjectId) {
        event.stopPropagation();
        const professors = DataManager.getProfessors();
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        const content = `
    <div style="padding: 1rem;" >
                <p style="margin-bottom: 1rem;">Selecciona profesor para <strong>${Utils.sanitizeHTML(subject.name)}</strong>:</p>
                <div style="display: flex; flex-direction: column; gap: 0.5rem; max-height: 300px; overflow-y: auto;">
                    ${professors.map(p => `
                        <div ${UIEvents.attr("click", function(event,captured){ProgramView.updateSubjectProfessor(String(captured[0]),String(captured[1]));}, [subjectId, p.id])} 
                             style="padding: 0.75rem; background: var(--bg-tertiary); border-radius: 6px; cursor: pointer;">
                            ${Utils.sanitizeHTML(p.firstName)} ${Utils.sanitizeHTML(p.lastName)}
                        </div>
                    `).join('')}
                    <div ${UIEvents.attr("click", function(event,captured){ProgramView.updateSubjectProfessor(String(captured[0]),null);}, [subjectId])} style="padding: 0.75rem; background: #f3f4f6; border-radius: 6px; cursor: pointer; color: #9ca3af;">Sin profesor</div>
                </div>
            </div>
    `;
        Utils.openModal('Cambiar Profesor', content);
    },

    updateSubjectProfessor(subjectId, professorId) {
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        DataManager.updateSubject(subjectId, { professorId: professorId });

        // Update all classes of this subject to new professor
        const classes = DataManager.getClassesBySubject(subjectId);
        classes.forEach(c => DataManager.updateClass(c.id, { professorId: professorId }));

        Utils.closeModal();
        this.renderSubjects();
        this.renderDashboard();
    },

    reorderSubjects(draggedInstanceId, targetInstanceId) {
        const draggedIndex = this.visibleSubjects.findIndex(i => String(i.instanceId) === String(draggedInstanceId));
        const targetIndex = this.visibleSubjects.findIndex(i => String(i.instanceId) === String(targetInstanceId));

        if (draggedIndex === -1 || targetIndex === -1) return;

        // Move item in array
        const [movedItem] = this.visibleSubjects.splice(draggedIndex, 1);
        this.visibleSubjects.splice(targetIndex, 0, movedItem);

        // Update storage
        const key = `program_visible_subjects_${this.currentFormation.id}`;
        BrowserState.setItem(key, JSON.stringify(this.visibleSubjects));

        this.renderSubjects();
    },

    getClassroomConflictsForSubject(classroomName, subjectId, monthPrefix) {
        const conflicts = [];
        if (!classroomName) return conflicts;

        // Get all classes in the system
        const allClasses = DataManager.getAll(DataManager.KEYS.CLASSES) || [];

        // Get classes of this subject in this month (our classes that we want to check)
        const subjectClasses = DataManager.getClassesByFormation(this.currentFormation.id)
            .filter(c => String(c.subjectId) === String(subjectId) && c.startDate && c.startDate.startsWith(monthPrefix));

        // Check each class of our subject
        for (const sCls of subjectClasses) {
            // Find any OTHER class in the system that:
            // 1. Is in the candidate classroom
            // 2. Is on the same startDate
            // 3. Overlaps in time
            // 4. Does NOT belong to the same subject
            const otherClasses = allClasses.filter(c => 
                c.classroom === classroomName &&
                c.startDate === sCls.startDate &&
                c.id !== sCls.id &&
                String(c.subjectId) !== String(subjectId)
            );

            for (const oCls of otherClasses) {
                if (ConflictService.timesOverlap(sCls.startTime, sCls.endTime, oCls.startTime, oCls.endTime)) {
                    const otherSubject = DataManager.getSubject(oCls.subjectId);
                    const otherFormation = DataManager.getById(DataManager.KEYS.FORMATIONS, oCls.formationId);
                    
                    const exists = conflicts.some(x => 
                        x.date === sCls.startDate && 
                        x.startTime === oCls.startTime && 
                        x.endTime === oCls.endTime &&
                        String(x.otherClass.subjectId) === String(oCls.subjectId)
                    );
                    if (!exists) {
                        conflicts.push({
                            date: sCls.startDate,
                            startTime: oCls.startTime,
                            endTime: oCls.endTime,
                            otherClass: oCls,
                            otherSubjectName: otherSubject ? otherSubject.name : 'Asignatura',
                            otherFormationName: otherFormation ? otherFormation.name : 'Formación',
                            myClass: sCls
                        });
                    }
                }
            }
        }
        return conflicts;
    },

    showClassroomConflictAlert(classroomName, subjectId, monthPrefix) {
        const conflicts = this.getClassroomConflictsForSubject(classroomName, subjectId, monthPrefix);
        if (conflicts.length === 0) return;

        let conflictsListHtml = conflicts.map(c => {
            const [y, m, d] = c.date.split('-');
            const dateStr = `${d}/${m}/${y}`;
            return `
                <div style="text-align: left; background: #fff5f5; border: 1px solid #feb2b2; border-radius: 8px; padding: 10px; margin-bottom: 8px; font-size: 0.85rem;">
                    <div style="font-weight: bold; color: #c53030; display: flex; align-items: center; gap: 6px;">
                        <i class="fas fa-calendar-day"></i> ${dateStr} (${c.startTime} - ${c.endTime})
                    </div>
                    <div style="color: #4a5568; margin-top: 5px;">
                        <strong>Grupo:</strong> ${c.otherFormationName}
                    </div>
                    <div style="color: #4a5568; margin-top: 2px;">
                        <strong>Asignatura:</strong> ${c.otherSubjectName}
                    </div>
                </div>
            `;
        }).join('');

        SafeHTML.fire({
            title: `<span style="color: #e53e3e; display: inline-flex; align-items: center; gap: 8px;"><i class="fas fa-exclamation-triangle"></i> Conflicto de Aula</span>`,
            html: `
                <p style="text-align: left; font-size: 0.9rem; color: #4a5568; margin-bottom: 1rem; line-height: 1.4;">
                    No se puede asignar el aula <strong>${classroomName}</strong> para este mes porque coincide con otro grupo en los siguientes días y horas:
                </p>
                <div style="max-height: 250px; overflow-y: auto; padding-right: 5px;">
                    ${conflictsListHtml}
                </div>
            `,
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#e53e3e'
        });
    },

    openClassroomPickerForSubject(event, subjectId, instanceId) {
        if (event) event.stopPropagation();
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        // Cargar zonas igual que InteractiveMapView (misma clave, mismo formato: cada
        // zona lleva su propia `floor`). Antes esta función NO diferenciaba planta —
        // pintaba TODAS las aulas (de la 1 y de la 2) superpuestas sobre el plano de la
        // planta 1 únicamente, así que las de planta 2 salían con coordenadas pensadas
        // para otro plano y se montaban encima de las de planta 1. Se usa FileStorage
        // (no BrowserState directo) para leer siempre el dato más fresco, igual que
        // InteractiveMapView.
        const rawSaved = FileStorage.getItem('ecpv_floorplan_zones');
        let zones = [
            { id: 'z1', name: 'Aula 1', x: 20, y: 15, width: 12, height: 18, floor: 1 },
            { id: 'z2', name: 'Aula 2', x: 33, y: 15, width: 12, height: 18, floor: 1 },
            { id: 'z3', name: 'Aula 3', x: 46, y: 15, width: 12, height: 18, floor: 1 },
            { id: 'z4', name: 'Aula 4', x: 59, y: 15, width: 12, height: 18, floor: 1 },
            { id: 'zp', name: 'Plató Principal', x: 28, y: 38, width: 28, height: 36, floor: 1 },
            { id: 'zs', name: 'Estudio de Sonido', x: 72, y: 15, width: 13, height: 18, floor: 1 },
            { id: 'ze', name: 'Sala de Edición', x: 70, y: 48, width: 15, height: 22, floor: 1 }
        ];
        if (rawSaved) { try { zones = JSON.parse(rawSaved); } catch (e) { } }
        zones.forEach(z => { if (z && !z.floor) z.floor = 1; });

        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

        // Planta inicial: si la asignatura ya tiene aula asignada este mes, abrir
        // directamente en la planta de esa aula; si no, planta 1 por defecto.
        const allClasses = DataManager.getAll(DataManager.KEYS.CLASSES) || [];
        const subjectMonthClasses = allClasses.filter(c =>
            String(c.subjectId) === String(subjectId) &&
            String(c.startDate || '').startsWith(monthPrefix)
        );
        const assignedClassrooms = [...new Set([
            ...(subject.classroom ? [subject.classroom] : []),
            ...subjectMonthClasses.map(c => c.classroom).filter(Boolean)
        ])];

        const assignedZone = assignedClassrooms.length > 0
            ? zones.find(z => assignedClassrooms.some(ac => ac && ac.trim().toLowerCase() === z.name.trim().toLowerCase()))
            : null;
        const initialFloor = assignedZone ? (assignedZone.floor || 1) : 1;

        // Contexto guardado en `this` para que switchClassroomPickerFloor pueda
        // reconstruir el HTML sin tener que volver a leer/parsear las zonas.
        this._classroomPicker = {
            subjectId,
            subjectName: subject.name,
            zones,
            monthPrefix,
            assignedClassrooms
        };

        SafeHTML.fire({
            title: '<i class="fas fa-map-marked-alt" style="margin-right:8px; color:#3b82f6;"></i>Seleccionar Aula',
            html: this._buildClassroomPickerHtml(initialFloor),
            width: '660px',
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            didOpen: () => {
                // Add hover style via CSS injection if not present
                if (!document.getElementById('classroom-swal-styles')) {
                    const style = document.createElement('style');
                    style.id = 'classroom-swal-styles';
                    style.textContent = '.classroom-map-swal .swal2-html-container { padding: 0 1.25rem; }';
                    document.head.appendChild(style);
                }
            }
        });
    },

    // Reconstruye el HTML del selector de aula para una planta dada — usa el contexto
    // guardado en this._classroomPicker por openClassroomPickerForSubject. Separado en
    // su propio método para poder llamarlo también desde switchClassroomPickerFloor
    // (cambiar de planta no reabre el modal, solo sustituye su contenido).
    _buildClassroomPickerHtml(floor) {
        const { subjectId, subjectName, zones, monthPrefix, assignedClassrooms = [] } = this._classroomPicker;

        const getStatus = (zoneName) => {
            const isAssigned = assignedClassrooms.some(ac => ac && ac.trim().toLowerCase() === zoneName.trim().toLowerCase());
            const conflicts = this.getClassroomConflictsForSubject(zoneName, subjectId, monthPrefix);
            const hasConflict = conflicts.length > 0;

            if (hasConflict) {
                const conflictDetails = conflicts.map(c => {
                    const [cy, cm, cd] = c.date.split('-');
                    return `${cd}/${cm}: ${c.otherFormationName} (${c.startTime}-${c.endTime})`;
                }).join('\n');

                const summaryLabel = conflicts.length === 1
                    ? `1 Conflicto`
                    : `${conflicts.length} Conflictos`;

                return {
                    isAssigned,
                    occupied: true,
                    label: isAssigned ? 'Asignada (Conflicto)' : 'Ocupada',
                    subLabel: summaryLabel,
                    details: conflictDetails
                };
            }

            return {
                isAssigned,
                occupied: false,
                label: isAssigned ? 'Asignada' : 'Libre',
                subLabel: isAssigned ? 'Aula actual' : ''
            };
        };

        const floorZones = zones.filter(z => (z.floor || 1) === floor);
        const roomsHtml = floorZones.map(z => {
            const st = getStatus(z.name);

            let bg, border, badgeBg, boxShadow, hoverBg;
            if (st.isAssigned && !st.occupied) {
                // Aula actualmente asignada a esta asignatura (sin conflicto)
                bg = 'rgba(37, 99, 235, 0.20)';
                border = '#2563eb';
                badgeBg = '#2563eb';
                boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.35), 0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                hoverBg = 'rgba(37, 99, 235, 0.32)';
            } else if (st.isAssigned && st.occupied) {
                // Aula asignada a esta asignatura pero con conflicto con otra formación
                bg = 'rgba(239, 68, 68, 0.22)';
                border = '#dc2626';
                badgeBg = '#dc2626';
                boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.35)';
                hoverBg = 'rgba(239, 68, 68, 0.35)';
            } else if (st.occupied) {
                // Otra aula ocupada / conflicto
                bg = 'rgba(239, 68, 68, 0.16)';
                border = '#ef4444';
                badgeBg = '#ef4444';
                boxShadow = 'none';
                hoverBg = 'rgba(239, 68, 68, 0.28)';
            } else {
                // Aula libre
                bg = 'rgba(16, 185, 129, 0.14)';
                border = '#10b981';
                badgeBg = '#10b981';
                boxShadow = 'none';
                hoverBg = 'rgba(16, 185, 129, 0.28)';
            }

            const cursor = 'pointer';
            const hover = `${UIEvents.attr("mouseover", function(event,captured){this.style.background=String(captured[0]);this.style.transform='scale(1.03)';}, [hoverBg])} ${UIEvents.attr("mouseout", function(event,captured){this.style.background=String(captured[0]);this.style.transform='scale(1)';}, [bg])}`;

            const clickAction = st.occupied
                ? () => ProgramView.showClassroomConflictAlert(z.name, subjectId, monthPrefix)
                : () => ProgramView._pickMapRoom(subjectId, z.name);

            const badgeIcon = st.isAssigned
                ? (st.occupied ? '<i class="fas fa-exclamation-triangle" style="margin-right:3px;"></i>' : '<i class="fas fa-check-circle" style="margin-right:3px;"></i>')
                : '';

            const tooltip = st.isAssigned
                ? (st.occupied ? `Aula actual de esta asignatura con conflicto:\n${st.details}` : `Aula asignada actualmente a esta asignatura (haz clic para mantener o reasignar)`)
                : (st.occupied ? `Conflictos:\n${st.details}` : `Haz clic para asignar`);

            return `<div ${UIEvents.attr("click", clickAction)} ${hover}
                         title="${Utils.sanitizeHTML(tooltip)}"
                         style="position:absolute; top:${z.y}%; left:${z.x}%; width:${z.width}%; height:${z.height}%;
                                background:${bg}; border:2.5px solid ${border}; border-radius:8px;
                                box-shadow:${boxShadow};
                                display:flex; flex-direction:column; justify-content:center; align-items:center;
                                cursor:${cursor}; transition:all 0.2s; text-align:center; padding:4px; box-sizing:border-box; z-index:${st.isAssigned ? 2 : 1};">
                        <span style="font-weight:800; font-size:0.75rem; color:#1e293b; pointer-events:none; line-height:1.2;">${Utils.sanitizeHTML(z.name)}</span>
                        <span style="background:${badgeBg}; color:white; font-size:0.56rem; font-weight:800; padding:1px 5px; border-radius:4px; margin-top:3px; text-transform:uppercase; pointer-events:none; display:inline-flex; align-items:center;">
                            ${badgeIcon}${Utils.sanitizeHTML(st.label)}
                        </span>
                        ${st.subLabel ? `<span style="font-size:0.56rem; color:${st.isAssigned && !st.occupied ? '#1d4ed8' : (st.occupied ? '#dc2626' : '#64748b')}; font-weight:800; margin-top:2px; pointer-events:none; background:${st.isAssigned && !st.occupied ? '#dbeafe' : 'transparent'}; padding:0 3px; border-radius:3px;">${st.subLabel}</span>` : ''}
                    </div>`;
        }).join('');

        const bgImage = floor === 2 ? '/assets/plano_instalaciones_p2.jpg' : '/assets/plano_instalaciones.png';

        return `
            <div style="margin-bottom:0.6rem; text-align:left;">
                <div style="display:flex; align-items:center; justify-content:space-between; gap:0.75rem; flex-wrap:wrap;">
                    <p style="margin:0; color:#475569; font-size:0.88rem;">
                        Selecciona el aula para <strong style="color:#1e293b;">${Utils.sanitizeHTML(subjectName)}</strong>:
                    </p>
                    <div style="display:inline-flex; border:1px solid #e2e8f0; border-radius:8px; overflow:hidden; flex-shrink:0;">
                        <button type="button" ${UIEvents.attr("click", function(event,captured){ProgramView.switchClassroomPickerFloor(1);}, [])}
                                style="border:none; padding:5px 14px; font-size:0.75rem; font-weight:700; cursor:pointer; transition:all 0.15s; background:${floor === 1 ? '#2563eb' : 'white'}; color:${floor === 1 ? 'white' : '#64748b'};">Planta 1</button>
                        <button type="button" ${UIEvents.attr("click", function(event,captured){ProgramView.switchClassroomPickerFloor(2);}, [])}
                                style="border:none; padding:5px 14px; font-size:0.75rem; font-weight:700; cursor:pointer; transition:all 0.15s; background:${floor === 2 ? '#2563eb' : 'white'}; color:${floor === 2 ? 'white' : '#64748b'};">Planta 2</button>
                    </div>
                </div>
                <div style="margin-top:6px; font-size:0.73rem; color:#64748b; display:flex; gap:1rem; flex-wrap:wrap; align-items:center;">
                    <span><span style="display:inline-block;width:11px;height:11px;border-radius:2px;background:#2563eb;margin-right:4px;vertical-align:middle;box-shadow:0 0 0 2px rgba(37,99,235,0.25);"></span><strong style="color:#1d4ed8;">Asignada</strong> (aula actual)</span>
                    <span><span style="display:inline-block;width:11px;height:11px;border-radius:2px;background:#10b981;margin-right:4px;vertical-align:middle;"></span>Libre — haz clic para asignar</span>
                    <span><span style="display:inline-block;width:11px;height:11px;border-radius:2px;background:#ef4444;margin-right:4px;vertical-align:middle;"></span>Conflicto — haz clic para ver detalles</span>
                </div>
            </div>
            <div style="position:relative; width:100%; aspect-ratio:1/1; border:1px solid #e2e8f0; border-radius:12px;
                         background-image:url('${bgImage}'); background-size:contain;
                         background-repeat:no-repeat; background-position:${floor === 2 ? 'top' : 'center'}; background-color:#fff;">
                ${roomsHtml || `<div style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#94a3b8; font-size:0.82rem;">Sin aulas dibujadas en esta planta</div>`}
            </div>
            <div style="margin-top:0.75rem; text-align:center;">
                <button ${UIEvents.attr("click", function(event,captured){ProgramView._pickMapRoom(String(captured[0]),'');}, [subjectId])}
                        style="background:none; border:1px solid #e2e8f0; border-radius:8px; padding:6px 16px; font-size:0.78rem; color:#94a3b8; cursor:pointer;">
                    <i class="fas fa-times" style="margin-right:4px;"></i>Sin aula asignada
                </button>
            </div>
        `;
    },

    // Cambia de planta dentro del modal ya abierto, sin cerrarlo (Swal.update sustituye
    // solo el html, conserva el resto de la configuración del modal).
    switchClassroomPickerFloor(floor) {
        if (!this._classroomPicker) return;
        Swal.update({ html: this._buildClassroomPickerHtml(floor) });
    },

    // Called when user clicks a free room on the map modal
    _pickMapRoom(subjectId, classroomName) {
        Swal.close();
        this.updateSubjectClassroom(subjectId, classroomName);
    },

    updateSubjectClassroom(subjectId, classroomName) {
        if (!this.currentFormation) return;

        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

        // Get classes for this subject and formation in the active month
        const classes = DataManager.getClassesByFormation(this.currentFormation.id)
            .filter(c => String(c.subjectId) === String(subjectId) && c.startDate && c.startDate.startsWith(monthPrefix));

        classes.forEach(c => {
            DataManager.updateClass(c.id, { classroom: classroomName });
        });

        // Guardar también en la asignatura como aula base
        DataManager.updateSubject(subjectId, { classroom: classroomName });

        const subject = DataManager.getSubject(subjectId);
        if (subject && ['COMÚN', 'PRACT', 'EXT'].includes(subject.type)) {
            const allFormations = DataManager.getFormations();
            allFormations.forEach(f => {
                if (f.id !== this.currentFormation.id) {
                    const fSubjects = DataManager.getSubjectsByFormation(f.id);
                    const matchingSub = fSubjects.find(s => s.name && subject.name && s.name.trim().toLowerCase() === subject.name.trim().toLowerCase());
                    if (matchingSub) {
                        DataManager.updateSubject(matchingSub.id, { classroom: classroomName });
                        const fClasses = DataManager.getClassesByFormation(f.id)
                            .filter(c => String(c.subjectId) === String(matchingSub.id) && c.startDate && c.startDate.startsWith(monthPrefix));
                        fClasses.forEach(c => {
                            DataManager.updateClass(c.id, { classroom: classroomName });
                        });
                    }
                }
            });
        }

        Utils.closeModal();
        this.renderSubjects();
        this.renderCalendar();
        Utils.showToast(`Aula '${classroomName || 'Sin aula'}' asignada a las clases del mes`, 'success');
    },

    // ── Popup de info del aula desde la lista de asignaturas (alumnos) ──────────
    showClassroomInfoFromSubject(event, subjectId) {
        if (event) event.stopPropagation();

        const year  = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

        // Build today's date string (use current month/year, day = today or 1st)
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

        // Find classes of this subject in this month that have a classroom assigned
        const allClasses = DataManager.getAll(DataManager.KEYS.CLASSES) || [];
        const subjectClasses = allClasses.filter(c =>
            String(c.subjectId) === String(subjectId) &&
            c.startDate && c.startDate.startsWith(monthPrefix) &&
            c.classroom
        );

        if (subjectClasses.length === 0) {
            Utils.showToast('Esta asignatura no tiene aula asignada este mes', 'info');
            return;
        }

        // Use the first classroom found and its date for the popup
        const cls      = subjectClasses[0];
        const classroom = cls.classroom;
        const dateStr   = cls.startDate;

        // Delegate to the popup already implemented in calendar.js
        ProgramView.showClassroomInfoPopup(classroom, dateStr, cls.startTime, cls.endTime);
    }
});
